// Copyright ©️ 2026 eVolve MEP, LLC

import './editable-cell.css';

import React, { useState, type ReactNode, type ComponentProps, useEffect, useCallback, useRef } from 'react';

import {
  Portal,
  Overlay,
  Popover,
  Flex,
  Card,
  Button,
  Box,
  Stack,
  Divider,
  type MantineSpacing,
  ComboboxClearButton,
  type PopoverProps,
} from '@mantine/core';

import { type EvolveIconName, EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { InfoIcon } from 'components/Mantine/InfoIcon';
import { FadingLoader } from 'helpers/ag-grid/EditableCells/FadingLoader';
import { emptyChildren } from 'helpers/emptyChildren';
import { isNotNil, isNil } from 'helpers/isNotNil';
import { strongIsEqual } from 'helpers/strongEntries';
import { useIsMobile } from 'hooks/useIsMobile';

export type EditorComponentProps<T> = {
  value?: T | null;
  onChange: (v: T | null) => void;
  autoFocus?: boolean;
  placeholder?: string;
  onSubmit?: () => void;
  error?: ReactNode;
};

export type EditorComponent<T> = React.FC<EditorComponentProps<T>>;

export type EditableCellProps<T, E extends EditorComponent<T>> = {
  /**
   * The component to use for editing the cell.
   * The component props *must* include `value` and `onChange`.
   */
  Editor: E;
  /**
   * Any additional props to pass into the `Editor` component.
   */
  editorProps?: Omit<ComponentProps<E>, 'value' | 'onChange'>;
  /**
   * If `true`, will close the popover (and trigger the Patch call)
   * when `onChange` is called via the Editor component.
   *
   * @defaultValue false
   */
  closeOnChange?: boolean;
  /**
   * Whether or not the cell is editable.
   */
  editable: boolean;
  /**
   * Initial value to use when editing begins.
   */
  initialValue: T | null | undefined;
  /**
   * Callback to save the new value
   */
  onSave: (v: T | null) => Promise<void>;
  /**
   * If true, the border will always display while editable.
   *
   * @defaultValue false
   */
  withBorder?: boolean;
  /**
   * Any footer to render below the Cancel/Save buttons.
   */
  footer?: ReactNode;
  /**
   * Any classes to apply to the `EditableCell` container.
   */
  className?: string;
  /**
   * Placeholder to pass across to the `Editor` component.
   * When omitted, the `Editor` keeps its own default placeholder.
   */
  placeholder?: string;
  /**
   * If true, will stretch to fit the width of the container.
   *
   * @defaultValue false
   * */
  fullWidth?: boolean;
  /**
   * Minimum width of the editable cell. Ignored if `fullWidth` is true.
   */
  miw?: number;
  /**
   * If true, prevents the user from submitting an empty value.
   *
   * @defaultValue false
   * */
  required?: boolean;
  /**
   * Returns a truthy `ReactNode` when the Editor's current value is invalid, matching Mantine `useForm` `validate` rules.
   * While it does, the cell disables Save and discards the edit if the popover closes.
   */
  validate?: (v: T | null) => ReactNode;
  /**
   * Text to display on the save button.
   *
   * @defaultValue 'Save'
   * */
  confirmationText?: string;
  /**
   * If true, will allow the user to clear the value by clicking on the clear button.
   */
  clearable?: boolean;
  /** @defaultValue 'sm' */
  dropdownPadding?: MantineSpacing;
  label?: ReactNode;
  labelTooltip?: ReactNode;
  disabled?: boolean;
  popoverPosition?: PopoverProps['position'];
  onOpen?: () => void;
  onClose?: () => void;
} & (
  | {
      /**
       * The value to render in the cell outside of the Editor.
       */
      renderValue: ReactNode;
      emptyState?: ReactNode;
      showEmptyState?: boolean;
      renderIcon?: never;
    }
  | {
      renderIcon: EvolveIconName;
      renderValue?: never;
      emptyState?: never;
      showEmptyState?: never;
    }
);

const extraCellPadding = 8;

/**
 * Popover used for in-line editing of cells.
 *
 * Use via `editableColDef`.
 */
export const EditableCell = <T, E extends EditorComponent<T> = EditorComponent<T>>({
  Editor,
  editorProps,
  renderValue,
  emptyState,
  showEmptyState,
  renderIcon,
  closeOnChange,
  initialValue = null,
  onSave,
  editable,
  placeholder,
  footer,
  className,
  clearable,
  withBorder = false,
  fullWidth = false,
  miw = 110,
  required = false,
  validate,
  disabled,
  confirmationText = 'Save',
  dropdownPadding = 'sm',
  label,
  labelTooltip,
  popoverPosition,
  onOpen,
  onClose: onCloseParent,
}: EditableCellProps<T, E>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const [opened, setOpened] = useState(false);
  const [value, setValue] = useState<T | null>(initialValue);
  const [saving, setSaving] = useState(false);
  const [shouldSave, setShouldSave] = useState(false);

  // Consider zero as a valid value for determining if the cell has a value.
  const hasValue = !!initialValue || initialValue === 0;
  const canEdit = editable && !disabled;
  const canOpenEditor = canEdit && !saving;
  const showDisabledState = editable && !!disabled;
  const showClearButton = !!clearable && !required && canEdit && hasValue && !saving;
  const showMobileEditAffordance = isMobile && canOpenEditor && !showClearButton;
  const valueIsEmpty = typeof value === 'number' || typeof value === 'boolean' ? isNil(value) : !value;

  const error = validate?.(value);
  const editorValid = !error;

  const saveButtonDisabled = !editorValid || (required && valueIsEmpty) || strongIsEqual(value, initialValue);

  const onClose = useCallback(() => {
    setOpened(false);
    onCloseParent?.();
  }, [onCloseParent]);

  useEffect(() => {
    if (disabled && opened) {
      setShouldSave(false);
      onClose();
    }
  }, [disabled, onClose, opened]);

  const onSaveWrapped = async (valueToSave: typeof value) => {
    setSaving(true);
    await Promise.resolve(onSave(valueToSave)).finally(() => setSaving(false));
  };

  return (
    <>
      {opened && (
        // Override the default `closeOnClickOutside` behavior
        // so that clicking on the Popover.Dropdown contents doesn't close the popover
        <Portal>
          <Overlay
            onClick={(e) => {
              onClose();
              e.stopPropagation();
            }}
            fixed
            opacity={0}
          />
        </Portal>
      )}
      <Popover
        opened={opened}
        onOpen={() => {
          setShouldSave(true);
          setValue(initialValue);
          onOpen?.();
        }}
        onChange={setOpened}
        closeOnClickOutside={false}
        onClose={async () => {
          const clearedEmptyString = value === '' && isNil(initialValue);
          if (!shouldSave || !editable || clearedEmptyString || saveButtonDisabled) {
            return;
          }
          await onSaveWrapped(value);
        }}
        keepMounted={false}
        position={popoverPosition ?? (fullWidth ? 'bottom' : renderIcon || isMobile ? 'bottom-end' : 'bottom-start')}
        trapFocus
        middlewares={{
          shift: {
            crossAxis: true,
          },
        }}
      >
        <Popover.Target>
          <Flex ref={containerRef} h="100%" w={fullWidth ? '100%' : undefined} align="center">
            {renderIcon ? (
              <Button
                disabled={!canEdit}
                variant="light"
                size="compact-sm"
                onClick={(e) => {
                  setOpened(true);
                  e.stopPropagation();
                }}
                loading={saving}
              >
                <EvolveIcon icon={renderIcon} size="sm" />
              </Button>
            ) : (
              <Card
                mx={editable || saving ? -extraCellPadding : undefined}
                px={editable || saving ? extraCellPadding : 0}
                py={4}
                w={`calc(100% + ${extraCellPadding * 2}px)`}
                h="calc(100% - 4px)"
                mih={26}
                miw={fullWidth ? undefined : miw}
                bg={editable ? 'white' : 'transparent'}
                onClick={(e) => {
                  if (canOpenEditor) {
                    setOpened(true);
                    e.stopPropagation();
                  }
                }}
                className={`editable-cell-renderer ${canEdit ? 'editable' : ''} ${opened ? 'active' : ''} ${className ?? ''}`}
                display="flex"
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderColor: opened
                    ? 'var(--mantine-primary-color-filled)'
                    : (editable && withBorder) || disabled
                      ? `var(--mantine-color-gray-${disabled ? 3 : 4})`
                      : undefined,
                  cursor: showDisabledState ? 'not-allowed' : undefined,
                  transition: 'border-color 100ms ease',
                  gap: 12,
                }}
                c={showDisabledState ? 'var(--mantine-color-disabled-color)' : 'inherit'}
              >
                <Stack gap={0} w="100%">
                  {!!label && (
                    <Flex fz="sm" c="dimmed" style={{ userSelect: 'none' }} align="center" gap={6}>
                      {label}
                      <InfoIcon label={labelTooltip} size={16} />
                    </Flex>
                  )}
                  <Box
                    w="100%"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {showEmptyState || emptyChildren(renderValue) ? (
                      <Flex fz="sm" c="dimmed" align="center" gap={8}>
                        {emptyState}
                      </Flex>
                    ) : (
                      renderValue
                    )}
                  </Box>
                </Stack>
                {showDisabledState && <Overlay bg="var(--mantine-color-disabled)" opacity={0.6} />}

                {showClearButton && (
                  <AgGridStyleTooltip label="Clear property" withArrow position="top-end">
                    <ComboboxClearButton
                      className="editable-clear-button"
                      onClear={() => {
                        void onSaveWrapped(null);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute',
                        right: 0,
                      }}
                    />
                  </AgGridStyleTooltip>
                )}

                {
                  /* Touch has no hover to reveal editability, so on mobile the loader's reserved
                    slot shows an edit affordance instead while idle (the clear button occupies
                    the same corner, so it takes precedence when shown). */
                  showMobileEditAffordance ? (
                    <Flex w={18} align="center" justify="center">
                      <EvolveIcon icon="Edit" size="sm" color="gray" />
                    </Flex>
                  ) : (
                    <FadingLoader visible={saving} flexProps={{ w: 18 }} size={18} />
                  )
                }
              </Card>
            )}
          </Flex>
        </Popover.Target>

        <Popover.Dropdown onClick={(e) => e.stopPropagation()} p={0}>
          <Stack p={dropdownPadding}>
            <Stack gap="xs" mah={400} miw={200} maw={350} style={{ overflow: 'hidden' }}>
              <Editor
                // Something super weird is happening with the types here,
                // but the typing of Props ensures `editorProps` is properly type checked
                // oxlint-disable-next-line typescript/no-explicit-any
                {...(editorProps as any)}
                value={value}
                onChange={(v) => {
                  setValue(v);
                  if (closeOnChange) onClose();
                }}
                onSubmit={() => {
                  if (!error) onClose();
                }}
                error={error}
                autoFocus
                data-autofocus
                // Only pass a defined placeholder, so an omitted one doesn't override the Editor's own default
                {...(isNotNil(placeholder) ? { placeholder } : {})}
              />
            </Stack>
          </Stack>
          {(!closeOnChange || isNotNil(footer)) && (
            <>
              <Divider />
              <Stack p="sm" gap="xs">
                {!closeOnChange && (
                  <Flex justify="space-between" gap="xs">
                    <Button
                      variant="light"
                      onClick={() => {
                        setShouldSave(false);
                        onClose();
                      }}
                      flex={1}
                      size="xs"
                    >
                      Cancel
                    </Button>
                    <Button onClick={onClose} disabled={saveButtonDisabled} flex={1} size="xs">
                      {confirmationText}
                    </Button>
                  </Flex>
                )}
                {isNotNil(footer) && <Flex>{footer}</Flex>}
              </Stack>
            </>
          )}
        </Popover.Dropdown>
      </Popover>
    </>
  );
};
